/**
 * Unified Data Types - Single source of truth from SPOG aggregation worker
 * Swiss minimalism: Only what we need, nothing more
 */

export interface WeeklyDataPoint {
  date: string;
  week_number: number;
  year: number;
  month: number;
  quarter: number;
  funding: FundingData;
  acquisitions: AcquisitionData;
}

export interface FundingData {
  amount: number;
  count: number;
  largest_deal: number;
  average_deal: number;
  categories: Record<string, number>;
  countries: Record<string, number>;
  stages: Record<string, number>;
  companies: string[];
  investors: string[];
}

export interface AcquisitionData {
  amount: number;
  count: number;
  largest_acquisition: number;
  average_value: number;
  categories: Record<string, number>;
  countries: Record<string, number>;
  types: Record<string, number>;
  companies: string[];
  acquirers: string[];
}

export interface DatasetMetadata {
  generated_at: string;
  source: string;
  record_count: number;
  date_range: {
    start: string;
    end: string;
  };
  totals: {
    funding_amount: number;
    acquisition_amount: number;
    funding_deals: number;
    acquisition_count: number;
    total_volume: number;
  };
}

export interface EnrichedDataset {
  metadata: DatasetMetadata;
  data: WeeklyDataPoint[];
}

export interface TimeframeFilter {
  label: string;
  weeks: number;
  key: string;
}

export interface ChartDataSeries {
  name: string;
  data: Array<[string, number]>;
  type?: 'line' | 'bar' | 'area' | 'heatmap' | 'calendar';
  color?: string;
  markPoints?: Array<{
    coord: [number, number];
    symbol: string;
    symbolSize: number;
    itemStyle: { color: string };
    label: {
      show: boolean;
      position: string;
      formatter: string;
      fontSize: number;
      color: string;
    };
  }>;
}

export const TIMEFRAMES: TimeframeFilter[] = [
  { label: '1 Week', weeks: 1, key: '1week' },
  { label: '2 Weeks', weeks: 2, key: '2weeks' },
  { label: '4 Weeks', weeks: 4, key: '4weeks' },
  { label: '1 Quarter', weeks: 13, key: '1quarter' },
  { label: '6 Months', weeks: 26, key: '6months' },
  { label: '9 Months', weeks: 39, key: '9months' },
  { label: '5 Quarters', weeks: 65, key: '5quarters' },
  { label: 'YTD', weeks: -2, key: 'ytd' },
  { label: 'T12M', weeks: 52, key: 'ttm' }
];

// Professional Typography System
export const TYPOGRAPHY = {
  fontFamily: 'Inter, sans-serif',
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 800,
  },
  sizes: {
    xs: '11px',
    sm: '13px',
    base: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '24px',
    '4xl': '28px',
    '5xl': '36px',
  },
  lineHeights: {
    tight: 1.1,
    snug: 1.2,
    normal: 1.4,
    relaxed: 1.6,
    loose: 1.8,
  },
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0em',
    wide: '0.05em',
    wider: '0.08em',
    widest: '0.1em',
  }
} as const;

export const BRAND_COLORS = {
  // RoS brand colors - Electric Purple + Black
  primary: '#633FF3',      // Electric Purple
  secondary: '#222222',    // RoS Black
  text: '#222222',         // Dark text
  textSecondary: '#666666', // Lighter text
  textMuted: '#999999',    // Muted text
  background: '#FFFFFF',   // White
  surface: '#FAFAFA',      // Light surface
  surfaceElevated: '#F5F5F5', // Elevated surface
  border: '#E8E8E8',       // Light grey
  borderHover: '#D0D0D0',  // Darker grey
  borderFocus: '#633FF3',  // Purple border for focus
  accent: '#00C851',       // Green for positive
  warning: '#FF4444',      // Red for negative
  info: '#17A2B8',         // Blue for info

  // Enhanced chart colors with event highlighting
  chart: {
    currentYear: '#633FF3',  // Electric Purple for current year
    previousYear: '#222222', // RoS Black for previous year
    neutral: '#666666',      // Neutral gray
    peak: '#FF6B6B',         // Peak highlight
    trough: '#4ECDC4',       // Trough highlight
    event: '#FFE66D',        // Market event highlight
    sparkline: '#633FF3',    // Sparkline color
    gradient: {
      primary: 'linear-gradient(135deg, #633FF3 0%, #7C3AED 100%)',
      secondary: 'linear-gradient(135deg, #222222 0%, #444444 100%)',
      success: 'linear-gradient(135deg, #00C851 0%, #28A745 100%)',
      heatmap: 'linear-gradient(90deg, rgba(99,63,243,0.1) 0%, rgba(99,63,243,1) 100%)',
    }
  },

  // Professional shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  }
} as const;

// Enhanced Chart Configuration Types
export interface ChartAnalytics {
  latestData: WeeklyDataPoint;
  previousData?: WeeklyDataPoint;
  totalFunding: number;
  totalAcquisitions: number;
  avgWeeklyFunding: number;
  avgWeeklyAcquisitions: number;
  fundingTrend: number;
  acquisitionsTrend: number;
  peaks: number[];
  troughs: number[];
  dataRange: {
    start: string;
    end: string;
  };
}

export interface MarketEvent {
  date: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  type: 'funding' | 'acquisition' | 'regulatory' | 'technology';
}

export interface EnhancedChartConfig {
  showSparklines?: boolean;
  showTrendIndicators?: boolean;
  enableBrushing?: boolean;
  showMiniMetrics?: boolean;
  highlightEvents?: boolean;
  showPeaksAndTroughs?: boolean;
  customEvents?: MarketEvent[];
  heatmapIntensity?: 'funding' | 'acquisitions' | 'combined';
}